import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchHitlItem } from "@/api/hitl";
import { getWorkflow, type WorkflowGraph } from "@/api/workflows";
import { fetchThreadTimeline, type TimelineEvent } from "@/api/observability";
import { fetchLeadDetail, fetchContact, type CrmLead, type CrmContact } from "@/api/crm";
import { fetchEventDetail, type CRMEventRecord } from "@/api/events/details";
import { fetchVenueDetail } from "@/api/venues";
import { fetchEpisodes, type MemoryEpisode } from "@/api/memory";
import type { HitlItem } from "@/types/hitl";

type HitlItemRelated = {
  workflow?: WorkflowGraph | null;
  threadTimeline?: TimelineEvent[];
  lead?: CrmLead | null;
  contact?: CrmContact | null;
  event?: CRMEventRecord | null;
  venue?: Record<string, unknown> | null;
  memoryEpisodes?: MemoryEpisode[];
};

function coerceId(value: unknown): string | null {
  if (typeof value === "number") return String(value);
  if (typeof value === "string" && value.trim().length) return value;
  return null;
}

export function useHitlItem(hitlId?: string | null) {
  const [item, setItem] = useState<HitlItem | null>(null);
  const [related, setRelated] = useState<HitlItemRelated>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (targetId?: string | null) => {
      if (!targetId) {
        setItem(null);
        setRelated({});
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const base = await fetchHitlItem(targetId);
        setItem(base);
        if (!base) {
          setRelated({});
          return;
        }
        const metadata = (base.metadata ?? {}) as Record<string, unknown>;
        const leadId = coerceId(metadata.lead_id ?? metadata.crm_lead_id);
        const contactId = coerceId(metadata.contact_id ?? metadata.crm_contact_id);
        const eventId = coerceId(metadata.crm_event_id ?? metadata.event_id);
        const venueId = coerceId(metadata.venue_id);
        const timelineTask =
          base.agent && base.thread_id
            ? fetchThreadTimeline(base.agent, base.thread_id).then((timeline) => ({
                key: "threadTimeline" as const,
                value: timeline,
              }))
            : null;
        const workflowTask = base.workflow_id
          ? getWorkflow(base.workflow_id).then((workflow) => ({
              key: "workflow" as const,
              value: workflow,
            }))
          : null;
        const leadTask = leadId
          ? fetchLeadDetail(leadId).then((lead) => ({
              key: "lead" as const,
              value: lead,
            }))
          : null;
        const contactNumericId = contactId && !Number.isNaN(Number(contactId)) ? Number(contactId) : null;
        const contactTask = contactNumericId
          ? fetchContact(contactNumericId).then((contact) => ({
              key: "contact" as const,
              value: contact,
            }))
          : null;
        const eventTask = eventId
          ? fetchEventDetail(eventId).then((record) => ({
              key: "event" as const,
              value: record,
            }))
          : null;
        const venueTask = venueId
          ? fetchVenueDetail(venueId).then((venue) => ({
              key: "venue" as const,
              value: venue,
            }))
          : null;
        const memoryTask =
          leadId || eventId || venueId
            ? fetchEpisodes({
                lead_id: leadId ?? undefined,
                event_id: eventId ?? undefined,
                venue_id: venueId ?? undefined,
                limit: 8,
              }).then((episodes) => ({
                key: "memoryEpisodes" as const,
                value: episodes,
              }))
            : null;
        const tasks = [timelineTask, workflowTask, leadTask, contactTask, eventTask, venueTask, memoryTask].filter(
          Boolean,
        ) as Array<Promise<{ key: keyof HitlItemRelated; value: unknown }>>;
        if (!tasks.length) {
          setRelated({});
          return;
        }
        const results = await Promise.allSettled(tasks);
        const nextRelated: HitlItemRelated = {};
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            const { key, value } = result.value;
            nextRelated[key] = value as never;
          }
        });
        setRelated(nextRelated);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load HITL details.");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!hitlId) {
      setItem(null);
      setRelated({});
      return;
    }
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await load(hitlId);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [hitlId, load]);

  const refresh = useCallback(async () => {
    await load(hitlId);
  }, [hitlId, load]);

  return useMemo(
    () => ({
      item,
      related,
      loading,
      error,
      refresh,
    }),
    [item, related, loading, error, refresh],
  );
}
